import { useMemo } from "react";
import Constants from "config/Constants";
import GroupsIcon from "components/Icons/GroupsIcon";
import ProgramsIcon from "components/Icons/ProgramsIcon";
import useHyroxTier from "hooks/useHyroxTier";
import useUpsellEntitlements from "hooks/useUpsellEntitlements";
import type { SidebarRoute, SidebarRouteGroup } from "../useSidebarRouteList/useSidebarRouteList";

const MANAGE_GROUP_TITLE = "global.manage_label";
const LIBRARIES_GROUP_TITLE = "global.libraries_label";

/**
 * Appends `item` to the group with `groupTitle`, creating the group if the
 * current entitlements filtered every one of its real routes out.
 */
function withItemInGroup(groups: SidebarRouteGroup[], groupTitle: string, item: SidebarRoute): SidebarRouteGroup[] {
  if (!groups.some((group) => group.title === groupTitle)) {
    return [...groups, { title: groupTitle, items: [item] }];
  }

  return groups.map((group) => (group.title === groupTitle ? { ...group, items: [...group.items, item] } : group));
}

/**
 * Adds locked upsell entries to the sidebar for feature bundles the org is not
 * entitled to, so coaches can discover what a higher tier offers instead of the
 * features simply being absent.
 *
 * This is a transform over the existing route list rather than a second sidebar:
 * when the build flag is off it returns its input untouched, which makes
 * "the sidebar looks exactly as it does today" structural rather than something
 * that has to be verified by eye.
 *
 * HYROX orgs are skipped entirely — they have their own locked-tier pattern
 * (see the `locked` flag in `useSidebarRouteList`) and the scope doc requires
 * HYROX pages not to change.
 */
function useUpsellSidebarRoutes(groups: SidebarRouteGroup[]): SidebarRouteGroup[] {
  const { isHyrox } = useHyroxTier();
  const { hasOutputProgram, hasGroupsAndMonitoring } = useUpsellEntitlements();

  return useMemo(() => {
    if (isHyrox) return groups;

    let result = groups;

    if (!hasGroupsAndMonitoring) {
      result = withItemInGroup(result, MANAGE_GROUP_TITLE, {
        route: Constants.routes.upsellGroupsAndMonitoring.route,
        title: "upsell.groups_and_monitoring_nav_label",
        icon: GroupsIcon,
        upsell: {
          tooltip: "upsell.groups_and_monitoring_nav_tooltip",
          feature: "GroupsAndMonitoring",
        },
      });
    }

    if (!hasOutputProgram) {
      result = withItemInGroup(result, LIBRARIES_GROUP_TITLE, {
        route: Constants.routes.upsellOutputProgram.route,
        title: "upsell.output_program_nav_label",
        icon: ProgramsIcon,
        upsell: {
          tooltip: "upsell.output_program_nav_tooltip",
          feature: "OutputProgram",
        },
      });
    }

    return result;
  }, [groups, isHyrox, hasGroupsAndMonitoring, hasOutputProgram]);
}

export default useUpsellSidebarRoutes;
